import Phaser from 'phaser';
import { Character, type CharacterDTO } from '@/network/RestApi';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useSkillStore } from '@/state/skillStore';
import { CLASS_COLORS, TEX } from '@/data/assetManifest';
import { isPhaserPointerOverBlockingUI } from '@/ui/pointerBlock';

const CARD_W_DESKTOP = 260;
const CARD_H_DESKTOP = 420;
const CARD_GAP_DESKTOP = 32;
const CARD_W_MOBILE = 200;
const CARD_H_MOBILE = 340;
const CARD_GAP_MOBILE = 16;
const PORTRAIT_KEY: Record<string, string> = {
  warrior: TEX.csWarrior,
  wizard: TEX.csWizard,
  archer: TEX.csArcher,
};
const CLASS_HEX: Record<string, number> = {
  warrior: 0xff5a4a,
  wizard: 0x6cb9ff,
  archer: 0x6dd97a,
};

export class CharacterSelectScene extends Phaser.Scene {
  private cards: Phaser.GameObjects.Container[] = [];
  private scrollContainer?: Phaser.GameObjects.Container;
  private scrollMin = 0;
  private scrollMax = 0;
  private scrollTargetX = 0;
  private leftArrow?: Phaser.GameObjects.Container;
  private rightArrow?: Phaser.GameObjects.Container;
  private dragStartX = 0;
  private dragStartScroll = 0;
  private isDragging = false;
  private dragMoved = false;
  private wheelHandler?: (p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => void;
  private cardW = CARD_W_DESKTOP;
  private cardH = CARD_H_DESKTOP;
  private cardGap = CARD_GAP_DESKTOP;
  private isCompact = false;

  constructor() { super('CharacterSelect'); }

  async create() {
    const W = this.scale.width;
    const H = this.scale.height;

    // Decide compact (mobile) layout based on the physical CSS viewport,
    // since Phaser uses Scale.FIT and internal coords are fixed at 1280x720.
    const physW = typeof window !== 'undefined' ? window.innerWidth : W;
    const physH = typeof window !== 'undefined' ? window.innerHeight : H;
    this.isCompact = physW < 768 || physH < 600 || physW < physH;
    this.cardW = this.isCompact ? CARD_W_MOBILE : CARD_W_DESKTOP;
    this.cardH = this.isCompact ? CARD_H_MOBILE : CARD_H_DESKTOP;
    this.cardGap = this.isCompact ? CARD_GAP_MOBILE : CARD_GAP_DESKTOP;

    // Backdrop: cathedral hall
    if (this.textures.exists(TEX.csBg)) {
      this.add.image(W / 2, H / 2, TEX.csBg).setDisplaySize(W, H);
    } else {
      this.add.image(W / 2, H / 2, 'bg_town').setDisplaySize(W, H).setAlpha(0.5);
    }

    // Vignette
    const grad = this.add.graphics();
    grad.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.65, 0.65, 0.0, 0.0);
    grad.fillRect(0, 0, W, 220);
    const grad2 = this.add.graphics();
    grad2.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.0, 0.0, 0.75, 0.75);
    grad2.fillRect(0, H - 200, W, 200);

    // Title banner
    this.drawTitleBanner(W / 2, 70);

    // Subtitle
    this.add.text(W / 2, 118, 'CHỌN ANH HÙNG CỦA BẠN', {
      fontFamily: '"Philosopher", "Cinzel", serif',
      fontSize: '16px',
      color: '#cdb175',
    }).setOrigin(0.5).setAlpha(0.9);

    try {
      const { characters } = await Character.list();
      this.renderList(characters);
    } catch (e) {
      useUIStore.getState().showToast(`Tải nhân vật thất bại: ${(e as Error).message}`, 'error');
      this.renderList([]);
    }
  }

  private drawTitleBanner(x: number, y: number) {
    const g = this.add.graphics();
    const bw = this.isCompact ? 360 : 560;
    const bh = this.isCompact ? 50 : 64;
    const fs = this.isCompact ? 28 : 38;
    // dark plate
    g.fillStyle(0x0a0a14, 0.85);
    g.fillRoundedRect(x - bw / 2, y - bh / 2, bw, bh, 6);
    // gold border
    g.lineStyle(2, 0xc9a24a, 1);
    g.strokeRoundedRect(x - bw / 2, y - bh / 2, bw, bh, 6);
    g.lineStyle(1, 0x6b5320, 1);
    g.strokeRoundedRect(x - bw / 2 + 6, y - bh / 2 + 6, bw - 12, bh - 12, 4);

    this.add.text(x, y, 'HERO WORLD', {
      fontFamily: '"Cinzel", "Philosopher", serif',
      fontSize: `${fs}px`,
      color: '#ffe7a3',
    }).setOrigin(0.5).setShadow(0, 2, '#000', 6, false, true);
  }

  private renderList(chars: CharacterDTO[]) {
    this.cards.forEach((c) => c.destroy());
    this.cards = [];
    this.scrollContainer?.destroy();
    this.leftArrow?.destroy();
    this.rightArrow?.destroy();
    if (this.wheelHandler) {
      this.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }

    const W = this.scale.width;
    const H = this.scale.height;
    const showCreate = chars.length < 5;
    const total = chars.length + (showCreate ? 1 : 0);
    const totalW = total * this.cardW + Math.max(0, total - 1) * this.cardGap;
    const y = H / 2 + 30;

    // Container holds all cards; positioned so child x=0 aligns to the left edge of layout
    const layoutLeft = W / 2 - totalW / 2;
    this.scrollContainer = this.add.container(layoutLeft, y);

    chars.forEach((c, i) => {
      const cx = i * (this.cardW + this.cardGap) + this.cardW / 2;
      const card = this.makeCharCard(cx, 0, c);
      this.scrollContainer!.add(card);
      this.cards.push(card);
    });
    if (showCreate) {
      const cx = chars.length * (this.cardW + this.cardGap) + this.cardW / 2;
      const create = this.makeCreateCard(cx, 0);
      this.scrollContainer!.add(create);
      this.cards.push(create);
    }

    // Compute scroll bounds. Cards are positioned with x being the card center,
    // so the leftmost card's left edge in container-local coords is 0 and the
    // rightmost card's right edge is totalW. Reserve space for side arrows.
    const arrowReserve = 90; // arrow center 40 + radius 28 + padding
    const leftBound = arrowReserve;
    const rightBound = W - arrowReserve;
    if (totalW > rightBound - leftBound) {
      // Layout overflows: allow scrolling
      // scrollMax: container.x such that first card's left edge sits at leftBound
      this.scrollMax = leftBound;
      // scrollMin: container.x such that last card's right edge sits at rightBound
      this.scrollMin = rightBound - totalW;
      // Start aligned to the left so first card is fully visible
      this.scrollContainer.x = this.scrollMax;
      this.scrollTargetX = this.scrollContainer.x;
      this.setupScrollInput();
      this.createArrows();
    } else {
      this.scrollMin = layoutLeft;
      this.scrollMax = layoutLeft;
      this.scrollTargetX = layoutLeft;
    }
  }

  private setupScrollInput() {
    // Mouse wheel scroll (horizontal via vertical wheel)
    this.wheelHandler = (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      if (!this.scrollContainer) return;
      this.scrollTargetX = Phaser.Math.Clamp(
        this.scrollTargetX - dy * 0.8,
        this.scrollMin,
        this.scrollMax,
      );
    };
    this.input.on('wheel', this.wheelHandler);

    // Drag to scroll on background
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (isPhaserPointerOverBlockingUI(p)) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.dragStartX = p.x;
      this.dragStartScroll = this.scrollContainer?.x ?? 0;
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.isDragging || !this.scrollContainer) return;
      if (!p.isDown) { this.isDragging = false; return; }
      const dx = p.x - this.dragStartX;
      if (Math.abs(dx) > 5) this.dragMoved = true;
      this.scrollTargetX = Phaser.Math.Clamp(
        this.dragStartScroll + dx,
        this.scrollMin,
        this.scrollMax,
      );
    });
    this.input.on('pointerup', () => {
      this.isDragging = false;
      // Reset dragMoved on next frame so card click handlers can read it
      this.time.delayedCall(0, () => { this.dragMoved = false; });
    });
  }

  private createArrows() {
    const W = this.scale.width;
    const H = this.scale.height;
    const y = H / 2 + 30;

    const makeArrow = (x: number, label: string, dir: number): Phaser.GameObjects.Container => {
      const cont = this.add.container(x, y);
      const g = this.add.graphics();
      const drawArrow = (hover: boolean) => {
        g.clear();
        g.fillStyle(0x0a0a14, hover ? 0.9 : 0.75);
        g.fillCircle(0, 0, 28);
        g.lineStyle(2, 0xc9a24a, 1);
        g.strokeCircle(0, 0, 28);
      };
      drawArrow(false);
      cont.add(g);
      const txt = this.add.text(0, 0, label, {
        fontFamily: '"Cinzel", serif',
        fontSize: '28px',
        color: '#ffe7a3',
      }).setOrigin(0.5);
      cont.add(txt);
      const hit = this.add.zone(0, 0, 60, 60).setInteractive({ useHandCursor: true });
      cont.add(hit);
      hit.on('pointerover', () => drawArrow(true));
      hit.on('pointerout', () => drawArrow(false));
      hit.on('pointerup', () => {
        if (!this.scrollContainer) return;
        this.scrollTargetX = Phaser.Math.Clamp(
          this.scrollTargetX + dir * (this.cardW + this.cardGap),
          this.scrollMin,
          this.scrollMax,
        );
      });
      return cont;
    };

    this.leftArrow = makeArrow(40, '◀', 1);
    this.rightArrow = makeArrow(W - 40, '▶', -1);
  }

  update() {
    if (!this.scrollContainer) return;
    if (this.scrollMin === this.scrollMax) return;
    // Smooth lerp to target
    this.scrollContainer.x = Phaser.Math.Linear(this.scrollContainer.x, this.scrollTargetX, 0.2);
  }

  private drawOrnateFrame(g: Phaser.GameObjects.Graphics, w: number, h: number, color: number, glow = false) {
    const x = -w / 2;
    const y = -h / 2;
    // Background plate (deep navy)
    g.fillStyle(0x0c1426, 0.92);
    g.fillRoundedRect(x, y, w, h, 8);
    // Inner gradient via overlapping rects
    g.fillStyle(0x1a2238, 0.55);
    g.fillRoundedRect(x + 4, y + 4, w - 8, h * 0.55, 6);
    // Outer brass border
    g.lineStyle(3, color, 1);
    g.strokeRoundedRect(x, y, w, h, 8);
    // Inner thin gold line
    g.lineStyle(1, 0xc9a24a, 0.85);
    g.strokeRoundedRect(x + 5, y + 5, w - 10, h - 10, 6);
    // Corner ornaments
    const corner = (cx: number, cy: number) => {
      g.fillStyle(0xc9a24a, 1);
      g.fillCircle(cx, cy, 4);
      g.fillStyle(0x6b5320, 1);
      g.fillCircle(cx, cy, 2);
    };
    corner(x + 10, y + 10); corner(x + w - 10, y + 10);
    corner(x + 10, y + h - 10); corner(x + w - 10, y + h - 10);

    if (glow) {
      g.lineStyle(2, color, 0.35);
      g.strokeRoundedRect(x - 4, y - 4, w + 8, h + 8, 10);
    }
  }

  private makeCharCard(x: number, y: number, c: CharacterDTO): Phaser.GameObjects.Container {
    const cont = this.add.container(x, y);
    const color = CLASS_HEX[c.class] ?? CLASS_COLORS[c.class];
    const W = this.cardW;
    const H = this.cardH;
    // Layout proportions (relative to card height)
    const portraitH = Math.round(H * 0.667);
    const portraitY = -Math.round(H * 0.12);
    const namePlateY = Math.round(H * 0.262);
    const nameY = Math.round(H * 0.305);
    const metaY = Math.round(H * 0.376);
    const btnY = Math.round(H * 0.417);
    const btnTxtY = Math.round(H * 0.464);
    const nameFs = this.isCompact ? 17 : 20;
    const metaFs = this.isCompact ? 11 : 13;
    const btnTxtFs = this.isCompact ? 14 : 16;

    const frame = this.add.graphics();
    this.drawOrnateFrame(frame, W, H, color, true);
    cont.add(frame);

    // Portrait
    const portraitKey = PORTRAIT_KEY[c.class];
    const portraitArea = { w: W - 28, h: portraitH };
    if (portraitKey && this.textures.exists(portraitKey)) {
      const tex = this.textures.get(portraitKey).getSourceImage() as HTMLImageElement;
      const scale = Math.min(portraitArea.w / tex.width, portraitArea.h / tex.height);
      const img = this.add.image(0, portraitY, portraitKey).setScale(scale);
      cont.add(img);
    } else {
      // fallback: tinted rect
      const r = this.add.rectangle(0, portraitY, portraitArea.w, portraitArea.h, color, 0.3);
      cont.add(r);
    }

    // Name plate
    const np = this.add.graphics();
    np.fillStyle(0x000000, 0.55);
    np.fillRoundedRect(-W / 2 + 14, namePlateY, W - 28, 36, 4);
    np.lineStyle(1, 0xc9a24a, 0.7);
    np.strokeRoundedRect(-W / 2 + 14, namePlateY, W - 28, 36, 4);
    cont.add(np);

    const name = this.add.text(0, nameY, c.name, {
      fontFamily: '"Cinzel", "Philosopher", serif',
      fontSize: `${nameFs}px`,
      color: '#ffe7a3',
    }).setOrigin(0.5).setShadow(0, 1, '#000', 4, false, true);
    cont.add(name);

    const meta = this.add.text(0, metaY, `${c.class.toUpperCase()}  ·  Lv ${c.level}  ·  Reset ${c.resetCount}`, {
      fontFamily: '"Philosopher", serif',
      fontSize: `${metaFs}px`,
      color: '#cdb175',
    }).setOrigin(0.5);
    cont.add(meta);

    // Play button (gold gradient look)
    const btnW = Math.min(180, W - 40); const btnH = 40;
    const btn = this.add.graphics();
    const drawBtn = (hover: boolean) => {
      btn.clear();
      btn.fillStyle(hover ? 0x3a2a10 : 0x2a1d0a, 1);
      btn.fillRoundedRect(-btnW / 2, btnY, btnW, btnH, 4);
      btn.lineStyle(2, 0xc9a24a, 1);
      btn.strokeRoundedRect(-btnW / 2, btnY, btnW, btnH, 4);
      btn.lineStyle(1, 0xffe7a3, 0.6);
      btn.strokeRoundedRect(-btnW / 2 + 3, btnY + 3, btnW - 6, btnH - 6, 3);
    };
    drawBtn(false);
    cont.add(btn);
    const btnTxt = this.add.text(0, btnTxtY, '▶  VÀO GAME', {
      fontFamily: '"Cinzel", "Philosopher", serif',
      fontSize: `${btnTxtFs}px`,
      color: '#ffe7a3',
    }).setOrigin(0.5);
    cont.add(btnTxt);

    // Hit area
    const hit = this.add.zone(0, 0, W, H).setInteractive({ useHandCursor: true });
    cont.add(hit);
    hit.on('pointerover', (p: Phaser.Input.Pointer) => {
      if (isPhaserPointerOverBlockingUI(p)) return;
      cont.setScale(1.03);
      drawBtn(true);
    });
    hit.on('pointerout', () => {
      cont.setScale(1);
      drawBtn(false);
    });
    hit.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (isPhaserPointerOverBlockingUI(p)) return;
      if (this.dragMoved) return;
      this.enterWorld(c);
    });

    // Subtle floating animation
    this.tweens.add({
      targets: cont,
      y: y - 4,
      duration: 1800 + Math.random() * 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return cont;
  }

  private makeCreateCard(x: number, y: number): Phaser.GameObjects.Container {
    const cont = this.add.container(x, y);
    const color = 0x4cd1ff;
    const W = this.cardW;
    const H = this.cardH;
    const plusFs = this.isCompact ? 96 : 120;
    const lblFs = this.isCompact ? 15 : 18;
    const descFs = this.isCompact ? 11 : 12;
    const lblY = Math.round(H * 0.214);
    const descY = Math.round(H * 0.310);
    const plusY = -Math.round(H * 0.095);

    const frame = this.add.graphics();
    this.drawOrnateFrame(frame, W, H, color, false);
    frame.lineStyle(2, 0x4cd1ff, 0.4);
    cont.add(frame);

    const plus = this.add.text(0, plusY, '+', {
      fontFamily: '"Cinzel", serif',
      fontSize: `${plusFs}px`,
      color: '#4cd1ff',
    }).setOrigin(0.5).setAlpha(0.85).setShadow(0, 0, '#4cd1ff', 12, true, true);
    cont.add(plus);

    const lbl = this.add.text(0, lblY, 'TẠO ANH HÙNG MỚI', {
      fontFamily: '"Cinzel", "Philosopher", serif',
      fontSize: `${lblFs}px`,
      color: '#cdb175',
    }).setOrigin(0.5);
    cont.add(lbl);

    const desc = this.add.text(0, descY, 'Khởi đầu một huyền thoại\ntrong thế giới Aetheria', {
      fontFamily: '"Philosopher", serif',
      fontSize: `${descFs}px`,
      color: '#7e8aa8',
      align: 'center',
    }).setOrigin(0.5);
    cont.add(desc);

    const hit = this.add.zone(0, 0, W, H).setInteractive({ useHandCursor: true });
    cont.add(hit);
    hit.on('pointerover', (p: Phaser.Input.Pointer) => {
      if (isPhaserPointerOverBlockingUI(p)) return;
      cont.setScale(1.03);
    });
    hit.on('pointerout', () => cont.setScale(1));
    hit.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (isPhaserPointerOverBlockingUI(p)) return;
      if (this.dragMoved) return;
      useUIStore.getState().set('menu', true);
    });
    return cont;
  }

  private enterWorld(c: CharacterDTO) {
    useCharacterStore.getState().setCurrent(c);
    void useSkillStore.getState().fetch();
    if (c.mapId === 1) this.scene.start('Town');
    else this.scene.start('Field');
  }
}
